import { get, post } from '@/utils/axios';
import { ElMessage } from 'element-plus';
import { isNull } from 'lodash-es';
import { ChatType } from '@/enum/chat';
import { isAssistantRole } from '@/hooks/teacher';
import useChatListStore from '@/store/chatList';
import pinia from '@/store';
class Message {
  static async getFreeChatList({
    page = 1,
    size = 20,
    token,
    signal,
    nickname,
    type = ChatType.ALL,
  }) {
    const params = {
      page,
      size,
      type,
    };

    if (nickname) {
      params.nickname = nickname;
    }
    let api = '/api/message/free_chat/list';
    const chatListStore = useChatListStore(pinia);

    if (isAssistantRole.value && chatListStore.route.params.role === 'user') {
      api = '/api/message/free_chat/user_list';
    }
    if (isAssistantRole.value && chatListStore.route.params.role === 'teacher') {
      api = '/api/message/free_chat/teacher_list';
    }

    const res = await get(api, params, {
      headers: {
        Authorization: token,
      },
      signal,
    });

    return res;
  }

  /**
   * 是否置頂
   * @returns {Promise<void>}
   */
  static async setChatToTop({ room_id, is_top, token }) {
    if (!room_id || isNull(is_top)) {
      ElMessage.error({
        message: '必須傳入room_id與is_top參數',
      });
      return;
    }

    return post(
      '/v1/freechat/set',
      {
        room_id,
        is_top,
      },
      {
        headers: {
          Authorization: token,
          'yqw-token': token,
        },
      },
    );
  }

  static getNotReadNum(token) {
    return get(
      '/api/comm/notice',
      {},
      {
        headers: {
          Authorization: token,
          'yqw-token': token,
        },
      },
    );
  }

  static createRoom({ user_id, token, signal }) {
    return post(
      '/v1/freechat/create',
      {
        user_id,
      },
      {
        headers: {
          Authorization: token,
          'yqw-token': token,
        },
        signal,
      },
    );
  }

  /**
   * 語音轉文字
   * @param room_id
   * @param msg_id
   * @param token
   * @returns {Promise<AxiosResponse<any>>}
   */
  static transformAudioToText({ room_id, msg_id, token }) {
    return get(
      '/api/comm/voice/trans',
      {
        room_id,
        msg_id,
        type: 1,
      },
      {
        headers: {
          Authorization: token,
          'yqw-token': token,
        },
      },
    );
  }

  static getCustomerInfo({ uid, token }) {
    return get(
      '/api/user/customer_info',
      {
        uid,
      },
      {
        headers: {
          Authorization: token,
          'yqw-token': token,
        },
      },
    );
  }

  // 獲取用户標籤
  static async getClientTag({ user_id, token }) {
    return get(
      '/api/user/tag',
      {
        uid: user_id,
      },
      {
        headers: {
          Authorization: token,
          'yqw-token': token,
        },
      },
    );
  }

  static resetRemainderChatNum({ room_id, token }) {
    return post(
      '/api/free_chat/reset/remainder_chat_num',
      {
        room_id,
      },
      {
        headers: {
          Authorization: token,
          'yqw-token': token,
        },
      },
    );
  }

  // 聊天列表-每日回訪
  static getEverydayReturnVisit({ token }) {
    return get(
      '/api/message/free_chat/everyday_return_visit',
      {},
      {
        headers: {
          Authorization: token,
          'yqw-token': token,
        },
      },
    );
  }

  // 助理角色調用，獲取消息type數組
  static getMsgCategory({ token }) {
    return get(
      '/api/message/free_chat/get_msg_category',
      {},
      {
        headers: {
          Authorization: token,
          'yqw-token': token,
        },
      },
    );
  }

  // 助理角色調用，退出人工模式
  static exitArtificial({ room_id, token }) {
    return post(
      '/v1/teacher/helper/exit_artificial',
      {
        room_id,
      },
      {
        headers: {
          Authorization: token,
          'yqw-token': token,
        },
      },
    );
  }

  // 助理角色調用，常見問題回覆列表
  static getCommonAskList({ token }) {
    return get(
      '/v1/teacher/helper/common_ask_list',
      {},
      {
        headers: {
          Authorization: token,
          'yqw-token': token,
        },
      },
    );
  }

  // 獲取老師助理在線狀態
  // https://yapi.linghit.cn/project/1431/interface/api/99738
  static getHelperOnlineStatus({ token }) {
    return get(
      '/v1/teacher/helper/get_today_data',
      {},
      {
        headers: {
          Authorization: token,
          'yqw-token': token,
        },
      },
    );
  }

  // 更新老師助理在線狀態設置
  // https://yapi.linghit.cn/project/1431/interface/api/99738
  static updateHelperOnlineStatus(token, postObj) {
    return post('/v1/teacher/helper/save_assistant_extend', postObj, {
      headers: {
        Authorization: token,
        'yqw-token': token,
      },
    });
  }

  // 發送常見問題回覆
  static sendQuickReply({ type, uid, token }) {
    return post(
      '/v1/teacher/helper/send_quick_reply',
      { type, uid },
      {
        headers: {
          Authorization: token,
          'yqw-token': token,
        },
      },
    );
  }

  static getReferralList({ page, size, token }) {
    return get(
      '/v1/teacher/helper/get_referral_list',
      { page, size },
      {
        headers: {
          Authorization: token,
          'yqw-token': token,
        },
      },
    );
  }

  static getGroupHistory({ group_id, group_type, previous_id, per_page, token }) {
    return get(
      '/v2/message/new_group_history',
      { group_id, group_type, previous_id, per_page },
      {
        headers: {
          Authorization: token,
          'yqw-token': token,
        },
      },
    );
  }

  // 敏感詞列表
  static getSensitiveWordList(data) {
    return get('/v2/common/sensitive_word', { ...data });
  }

  // 獲取聊天室 AI 實時翻譯設置
  static getRealtimeTranslateInfo({ room_id, token }) {
    return get(
      '/api.service/teacher/chatroom/realtime_translate/info',
      { room_id },
      {
        headers: {
          Authorization: token,
          'yqw-token': token,
        },
        successCodes: [0, 1, 200],
      },
    );
  }

  // 保存聊天室 AI 實時翻譯設置
  static saveRealtimeTranslate({
    room_id,
    accept_status,
    send_status,
    accept_show_origin,
    accept_lang,
    send_lang,
    token,
  }) {
    return post(
      '/api.service/teacher/chatroom/realtime_translate',
      {
        room_id,
        accept_status,
        send_status,
        accept_show_origin,
        accept_lang,
        send_lang,
      },
      {
        headers: {
          Authorization: token,
          'yqw-token': token,
        },
        successCodes: [0, 1, 200],
      },
    );
  }

  // 翻譯文本
  static translate({ text, translate_language, msg_id, room_id, token }) {
    const payload = { text };
    if (translate_language) {
      payload.translate_language = translate_language;
    }
    if (msg_id) {
      payload.msg_id = msg_id;
    }
    if (room_id) {
      payload.room_id = room_id;
    }

    return post('/api.service/teacher/translate', payload, {
      headers: {
        Authorization: token,
        'yqw-token': token,
      },
      successCodes: [0, 1, 200],
    });
  }
}

export default Message;
